import { createRoot, createSignal } from 'solid-js';
import { deck } from '~/lib/deck';
import { economy } from '~/lib/economy';
import { geolocation } from '~/lib/geolocation';
import { player } from '~/lib/player';
import type { HackBand } from '~/lib/resolution';

/** Economy log buffer cap. At the 1.5s sample rate, 5000 rows ≈ 2 hours. */
export const ECON_LOG_CAP = 5000;
/** Periodic sample interval — matched to the heat logger's sample rate so the
 *  two exports align on a common time axis (the dual price-multiplier decay and
 *  the heat decay are then readable side by side). */
export const ECON_LOG_SAMPLE_MS = 1500;

export type EconEventType = 'earn' | 'spend' | 'dual_success' | 'dual_lockout' | 'sample';

export interface EconLogRow {
  timestamp: number;
  eventType: EconEventType;
  /** ¤ delta: positive on earn, negative on spend; null on state/sample rows. */
  amount: number | null;
  balanceAfter: number;
  /** Spend target: a tool id or `heat_reduction`; null on non-spend rows. */
  detail: string | null;
  band: HackBand | null;
  targetTier: number | null;
  heatCleared: number | null;
  nodeId: string | null;
  isDual: boolean | null;
  nodePriceMult: number | null;
  nodeLockedUntil: number | null;
  ownedToolCount: number;
  playerLat: number | null;
  playerLng: number | null;
  isSimulated: boolean;
}

/** Aggregate view of the economy log for the live DIAG readout. Computed from
 *  the buffer on render, never on the economic actions themselves. */
export interface EconSummary {
  earned: number; // gross ¤ from hack payouts (positive earn deltas)
  penalties: number; // gross ¤ burned on badfails (negative earn deltas)
  spent: number; // gross ¤ on purchases (tools + heat reduction)
  net: number; // total logged balance delta (earn + spend)
  byTier: Record<number, { count: number; median: number }>;
}

function median(xs: number[]): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

/** PURE: reduce the log buffer to the live-readout summary. Median payout is
 *  taken over ALL earn rows at a tier (every band), i.e. the realistic per-hack
 *  ¤ outcome for that tier, which is what the tuning pass wants. */
export function summarizeEcon(rows: EconLogRow[]): EconSummary {
  let earned = 0;
  let penalties = 0;
  let spent = 0;
  let net = 0;
  const tierPayouts: Record<number, number[]> = {};
  for (const r of rows) {
    if (r.eventType === 'earn' && r.amount !== null) {
      net += r.amount;
      if (r.amount >= 0) earned += r.amount;
      else penalties += -r.amount;
      if (r.targetTier !== null) (tierPayouts[r.targetTier] ??= []).push(r.amount);
    } else if (r.eventType === 'spend' && r.amount !== null) {
      net += r.amount;
      spent += -r.amount;
    }
  }
  const byTier: Record<number, { count: number; median: number }> = {};
  for (const [tier, xs] of Object.entries(tierPayouts)) {
    byTier[Number(tier)] = { count: xs.length, median: Math.round(median(xs)) };
  }
  return { earned, penalties, spent, net, byTier };
}

/** DIAG-only economy logger. Observes economy/player/deck/heat state READ-ONLY
 *  (never mutates a balance, owned tool, dual state, or heat): each row
 *  snapshots the current ¤ balance, owned-tool count, and player position, and
 *  the involved node's dual state (price multiplier, lockout). Event rows are
 *  appended imperatively by the HackModal (earn + dual consequence) and the
 *  VendorModal (spend) AFTER their store updates land; sample rows are appended
 *  periodically so the balance curve and the multiplier-decay slope between
 *  events are visible. Mirrors the heat logger. Module-level singleton;
 *  persistence is not needed (diagnostics only). */
function createEconLog() {
  const [rows, setRows] = createSignal<EconLogRow[]>([]);

  const append = (row: EconLogRow): void => {
    setRows((rs) => {
      const next = [...rs, row];
      if (next.length > ECON_LOG_CAP) next.splice(0, next.length - ECON_LOG_CAP);
      return next;
    });
  };

  const playerSnapshot = () => {
    const f = geolocation.latest();
    return f ? { lat: f.lat, lng: f.lng, simulated: f.simulated } : null;
  };

  const base = (eventType: EconEventType): EconLogRow => {
    const p = playerSnapshot();
    return {
      timestamp: Date.now(),
      eventType,
      amount: null,
      balanceAfter: player.credits(),
      detail: null,
      band: null,
      targetTier: null,
      heatCleared: null,
      nodeId: null,
      isDual: null,
      nodePriceMult: null,
      nodeLockedUntil: null,
      ownedToolCount: deck.ownedTools().length,
      playerLat: p?.lat ?? null,
      playerLng: p?.lng ?? null,
      isSimulated: p?.simulated ?? false,
    };
  };

  /** Read-only snapshot of a node's dual state for the node-bearing columns. */
  const nodeCols = (
    nodeId: string,
    isDual: boolean,
  ): Pick<EconLogRow, 'nodeId' | 'isDual' | 'nodePriceMult' | 'nodeLockedUntil'> => {
    const now = Date.now();
    const lockedUntil = economy.lockoutUntil(nodeId);
    return {
      nodeId,
      isDual,
      nodePriceMult: economy.priceMultiplier(nodeId, now),
      nodeLockedUntil: lockedUntil > now ? lockedUntil : null,
    };
  };

  /** A hack payout (target or dual node). `amount` is the net ¤ delta applied
   *  (payout − penalty), so a badfail is a negative earn. */
  const logEarn = (opts: {
    band: HackBand;
    amount: number;
    tier: number;
    nodeId: string;
    isDual: boolean;
  }): void => {
    append({
      ...base('earn'),
      amount: opts.amount,
      band: opts.band,
      targetTier: opts.tier,
      ...nodeCols(opts.nodeId, opts.isDual),
    });
  };

  /** A ¤ spend: a tool purchase (`detail` = tool id) or heat reduction
   *  (`detail` = `heat_reduction`, `heatCleared` set). `amount` is negative. */
  const logSpend = (opts: {
    amount: number;
    detail: string;
    nodeId: string;
    isDual: boolean;
    heatCleared?: number;
  }): void => {
    append({
      ...base('spend'),
      amount: opts.amount,
      detail: opts.detail,
      heatCleared: opts.heatCleared ?? null,
      ...nodeCols(opts.nodeId, opts.isDual),
    });
  };

  /** A dual-node hack consequence, logged AFTER `economy.onDualHack` so the
   *  node columns reflect the post-event state: success → the raised (decaying)
   *  price multiplier; failure → the new lockout expiry. */
  const logDual = (nodeId: string, band: HackBand): void => {
    const success = band === 'clean' || band === 'partial';
    append({
      ...base(success ? 'dual_success' : 'dual_lockout'),
      band,
      ...nodeCols(nodeId, true),
    });
  };

  /** Periodic sample: one balance row (node columns blank) plus one row per
   *  still-active dual node (raised multiplier or live lockout) so the decay
   *  slope is reconstructable per node. Skips entirely while idle (no logged
   *  activity yet and no active dual state), mirroring the heat logger's
   *  skip-idle rule. */
  const sample = (): void => {
    const now = Date.now();
    const activeIds = Object.keys(economy.states());
    if (rows().length === 0 && activeIds.length === 0) return;
    append(base('sample'));
    for (const id of activeIds) {
      const mult = economy.priceMultiplier(id, now);
      const locked = now < economy.lockoutUntil(id);
      if (mult <= 1 && !locked) continue; // fully decayed and unlocked → inactive
      append({
        ...base('sample'),
        nodeId: id,
        isDual: true,
        nodePriceMult: mult,
        nodeLockedUntil: locked ? economy.lockoutUntil(id) : null,
      });
    }
  };

  if (typeof window !== 'undefined') {
    setInterval(sample, ECON_LOG_SAMPLE_MS);
  }

  return {
    rows,
    count: () => rows().length,
    clear: (): void => {
      setRows([]);
    },
    sample,
    logEarn,
    logSpend,
    logDual,
  };
}

export const econLog = createRoot(() => createEconLog());
